import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Sparkles, Trash2 } from 'lucide-react'
import { PageHeaderBand } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { ApiError } from '@/lib/api/jsonFetch'
import { useCurrentUser } from '@/shell/services/auth'
import { CarlaChatPane } from '../components/CarlaChatPane'
import {
  useConversationMessages,
  useConversations,
  useDeleteConversation,
  type ConversationListItem,
} from '../api/queries'

/**
 * Per-user localStorage key for the last-active conversation. Scoped by
 * Odoo uid so multiple users on the same browser don't see each other's
 * conversations. Read on mount → drives auto-select; written on every
 * setSelectedId so refresh / navigate-away-and-back restores cleanly.
 */
function lastConvStorageKey(uid: number | string | undefined): string | null {
  if (uid === undefined || uid === null) return null
  return `carla:lastConv:${uid}`
}

/**
 * CARLA — AI coworker. Two-pane layout: recent conversations on the
 * left, chat pane on the right. Starting state is a fresh "new
 * conversation" — on first message the middleware issues a conversation
 * id via the X-Conversation-ID response header and this page picks it up
 * to highlight the new row in the list.
 *
 * Access: every role flagged `can_chat: True` in middleware rbac.py
 * (admin, sales, operations, regulatory, finance). `can_confirm_actions`
 * is True for all of those same roles in rbac.py, so every user reaching
 * this page can confirm staged actions — the local `canConfirmActions`
 * below stays literal `true`. If rbac.py ever splits those flags (a role
 * that can chat but not confirm), wire this value through the AuthUser
 * type from /api/auth/me.
 */
export function CarlaPage() {
  const canConfirmActions = true

  // Per-user localStorage scope key. By the time CarlaPage renders the
  // Shell has already gated on user existence, so `user` is reliably
  // available — but stay defensive (storageKey null = skip persistence).
  const { user } = useCurrentUser()
  const storageKey = lastConvStorageKey(user?.uid)

  const conversationsQuery = useConversations()
  const conversations = useMemo(
    () => conversationsQuery.data ?? [],
    [conversationsQuery.data],
  )

  const [selectedId, setSelectedIdRaw] = useState<string | null>(null)
  // Bumped when the user explicitly starts a new conversation or
  // switches to a different one — forces CarlaChatPane to remount with
  // a clean useCarlaChat hook state. Without this, "+ New" can leave
  // the pane showing the previous conversation's messages because the
  // hook's internal conversationId persists across prop changes.
  const [chatInstanceKey, setChatInstanceKey] = useState(0)
  // Auto-select-last-conversation runs once per mount lifecycle.
  // Flipped true at the START of the effect (not the end) so concurrent
  // setSelectedId calls — e.g. user clicks +New while we're choosing —
  // don't get re-fired by a re-run of this effect.
  const autoSelectedRef = useRef(false)

  /** setSelectedId wrapper that keeps localStorage in sync. */
  const setSelectedId = useCallback(
    (id: string | null) => {
      setSelectedIdRaw(id)
      if (!storageKey) return
      try {
        if (id) localStorage.setItem(storageKey, id)
        else localStorage.removeItem(storageKey)
      } catch {
        // private browsing / disabled storage — best-effort
      }
    },
    [storageKey],
  )

  // Auto-select on first mount: prefer persisted localStorage id (if it
  // still exists in the conversations list), fall back to the most-recent
  // conversation. Effect waits for BOTH user (storageKey) AND list to
  // load so a single decision commits — no fan-out, no race window where
  // the wrong default flashes before the right one settles.
  useEffect(() => {
    if (autoSelectedRef.current) return
    if (!storageKey) return // user not loaded yet
    if (conversationsQuery.isLoading) return // list not loaded yet

    autoSelectedRef.current = true

    // If the user has already started interacting (e.g. mid-stream
    // created a new conversation) — respect their choice, don't override.
    if (selectedId !== null) return

    let target: string | null = null
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored && conversations.some((c) => c.id === stored)) {
        target = stored
      } else {
        // Stale or no-prior — fall back to most recent
        target = conversations[0]?.id ?? null
        if (stored) {
          // Drop the stale entry so we don't keep checking it
          localStorage.removeItem(storageKey)
        }
      }
    } catch {
      target = conversations[0]?.id ?? null
    }

    if (target) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs selection to the conversations list once it's loaded; standard fetch-then-pick pattern.
      setSelectedIdRaw(target)
      // Persist the chosen target (covers the most-recent fallback path).
      try {
        if (storageKey) localStorage.setItem(storageKey, target)
      } catch {
        // best-effort
      }
      setChatInstanceKey((k) => k + 1)
    }
  }, [storageKey, conversationsQuery.isLoading, conversations, selectedId])

  const selectedMessages = useConversationMessages(selectedId)

  // Recover from a 404 on the messages query — the persisted/auto-
  // selected id refers to a conversation that no longer exists
  // server-side. Clear the stale id and fall back to the most recent
  // (or null if no others). Single recovery, no loop — depends on
  // selectedMessages.error which only fires once per failed fetch.
  useEffect(() => {
    if (!selectedMessages.isError) return
    if (!selectedId) return
    const err = selectedMessages.error
    if (!(err instanceof ApiError) || err.status !== 404) return

    const fallback = conversations[0]?.id ?? null
    if (storageKey) {
      try {
        if (fallback) localStorage.setItem(storageKey, fallback)
        else localStorage.removeItem(storageKey)
      } catch {
        // best-effort
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- recovery path on 404 from the messages query; one-shot, deps gate prevents loops.
    setSelectedIdRaw(fallback)
    setChatInstanceKey((k) => k + 1)
  }, [
    selectedMessages.isError,
    selectedMessages.error,
    selectedId,
    conversations,
    storageKey,
  ])

  const seedHistory = useMemo(
    () => selectedMessages.data?.messages ?? [],
    [selectedMessages.data],
  )

  const deleteMutation = useDeleteConversation()

  function handleSelect(id: string | null) {
    setSelectedId(id)
    setChatInstanceKey((k) => k + 1)
  }
  function handleNewConversation() {
    // User explicitly chose +New — clear localStorage so a refresh /
    // navigate-back doesn't re-select the abandoned conversation.
    setSelectedId(null)
    setChatInstanceKey((k) => k + 1)
  }
  function handleNewConversationCreated(id: string) {
    setSelectedId(id) // persist newly-minted id immediately
    void conversationsQuery.refetch()
  }
  function handleDelete(id: string) {
    if (!confirm('Delete this conversation? The history cannot be recovered.')) return
    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (selectedId === id) setSelectedId(null)
      },
    })
  }

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="CARLA"
        description="AI coworker · admin only. Ask about orders, inventory, margins, CofAs — or run confirmed actions in Odoo."
      />

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[280px] flex-shrink-0 flex-col border-r border-border-subtle bg-surface-sunk">
          <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
            <span className="text-role-section-label">Conversations</span>
            <button
              type="button"
              onClick={handleNewConversation}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xs border border-border-default bg-surface px-2 py-1 text-xs font-semibold text-primary hover:bg-surface-hover"
            >
              <Plus size={12} /> New
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversationsQuery.isLoading ? (
              <p className="px-3 py-4 text-xs text-tertiary">Loading…</p>
            ) : conversations.length === 0 ? (
              <p className="px-3 py-4 text-xs text-tertiary">
                No conversations yet. Send CARLA a message to start.
              </p>
            ) : (
              <ul className="m-0 flex flex-col p-0">
                {conversations.map((c) => (
                  <li key={c.id} className="list-none">
                    <ConversationRow
                      item={c}
                      selected={selectedId === c.id}
                      onSelect={() => handleSelect(c.id)}
                      onDelete={() => handleDelete(c.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <section className="flex min-w-0 flex-1">
          <CarlaChatPane
            key={chatInstanceKey}
            conversationId={selectedId}
            seedHistory={seedHistory}
            isLoadingSeed={Boolean(selectedId) && selectedMessages.isLoading}
            canConfirmActions={canConfirmActions}
            onNewConversationCreated={handleNewConversationCreated}
            onMessageSent={() => void conversationsQuery.refetch()}
            onNewConversationRequested={handleNewConversation}
          />
        </section>
      </div>
    </div>
  )
}

function ConversationRow({
  item,
  selected,
  onSelect,
  onDelete,
}: {
  item: ConversationListItem
  selected: boolean
  onSelect: () => void
  onDelete: () => void
}) {
  const title = item.title?.trim() || fallbackTitle(item)
  const when = item.last_message_at ?? item.created_at
  return (
    <div
      className={cn(
        'group flex cursor-pointer items-start gap-1.5 border-b border-border-subtle border-l-2 border-l-transparent px-3 py-2 transition-colors duration-fast',
        'hover:bg-surface-hover',
        selected && 'border-l-accent bg-accent-subtle',
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <Sparkles
        size={12}
        className={cn('mt-0.5 flex-shrink-0', selected ? 'text-accent' : 'text-tertiary')}
      />
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm font-medium text-primary">{title}</p>
        <p className="text-role-metadata m-0">{formatRelative(when)}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        aria-label="Delete conversation"
        className="invisible inline-flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-danger-bg hover:text-danger-text group-hover:visible"
      >
        <Trash2 size={12} />
      </button>
    </div>
  )
}

function fallbackTitle(c: ConversationListItem): string {
  const d = c.last_message_at ?? c.created_at
  return `Conversation · ${new Date(d).toLocaleDateString()}`
}

function formatRelative(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString()
}
